import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './services/api';
import { TokenSocket } from './services/tokenWebSocket';
import {
    OracleQuote,
    OrderBook,
    SwapData,
    TokenInfo,
} from './types';
import OrderBookView from './components/OrderBook';
import TradeHistory from './components/TradeHistory';
import OracleTable from './components/OracleTable';
import { ChainSelector, TokenSelector } from './components/Selectors';
import './App.css';

const MAX_TRADES = 100;

export default function App() {
    const [chains, setChains] = useState<string[]>([]);
    const [chain, setChain] = useState<string>('');
    const [coins, setCoins] = useState<TokenInfo[]>([]);
    const [token, setToken] = useState<TokenInfo | null>(null);
    const [orderBooks, setOrderBooks] = useState<Record<string, OrderBook>>({});
    const [tradesByToken, setTradesByToken] = useState<Record<string, SwapData[]>>({});
    const [oracles, setOracles] = useState<OracleQuote[]>([]);
    const [connectedTokens, setConnectedTokens] = useState<Record<string, boolean>>({});
    const [error, setError] = useState<string | null>(null);
    const socketsRef = useRef<TokenSocket[]>([]);

    // initial chain list
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const h = await api.health();
                if (cancelled) return;
                setChains(h.chains);
                setChain((c) => c || h.default_chain);
            } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // load coins / oracles per chain — clear stale token first so the
    // socket effect doesn't briefly subscribe to (newChain, oldChainToken)
    useEffect(() => {
        if (!chain) return;
        setToken((cur) => (cur && cur.chain === chain ? cur : null));
        let cancelled = false;
        (async () => {
            try {
                const cs = await api.coins(chain);
                if (cancelled) return;
                setCoins(cs);
                setToken((cur) => {
                    if (cur && cs.find((c) => c.address === cur.address)) return cur;
                    return cs[0] || null;
                });
                const qs = await api.oracles(chain);
                if (cancelled) return;
                setOracles(qs);
            } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
            }
        })();
        const refresh = window.setInterval(async () => {
            try {
                const qs = await api.oracles(chain);
                setOracles(qs);
            } catch {
                /* ignore */
            }
        }, 5000);
        return () => {
            cancelled = true;
            window.clearInterval(refresh);
        };
    }, [chain]);

    // Sockets are keyed on the chain's token set, not on the selected token:
    // switching tokens then renders an already-warm book instead of waiting for
    // a fresh subscription and snapshot. Only the visible chain is subscribed,
    // so the backend can idle the other one.
    const coinKey = useMemo(() => coins.map((c) => c.address).join(','), [coins]);

    useEffect(() => {
        setOrderBooks({});
        setTradesByToken({});
        setConnectedTokens({});
        if (!chain || coins.length === 0) return;
        // skip while a chain switch is still settling (coins belong to the old chain)
        if (coins.some((c) => c.chain && c.chain !== chain)) return;

        const sockets = coins.map((coin) => {
            const address = coin.address;
            const sock = new TokenSocket(chain, address, {
                onStatus: (up) =>
                    setConnectedTokens((prev) => ({ ...prev, [address]: up })),
                onOrderBook: (ob) =>
                    setOrderBooks((prev) => ({ ...prev, [address]: ob })),
                onTrade: (t) =>
                    setTradesByToken((prev) => ({
                        ...prev,
                        [address]: [t, ...(prev[address] ?? [])].slice(0, MAX_TRADES),
                    })),
                onError: (e) => setError(e.message),
            });
            sock.connect();
            return sock;
        });
        socketsRef.current = sockets;

        return () => {
            sockets.forEach((sock) => sock.close());
            socketsRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chain, coinKey]);

    const orderBook = token ? orderBooks[token.address] ?? null : null;
    const trades = token ? tradesByToken[token.address] ?? [] : [];

    const liveCount = coins.filter((c) => connectedTokens[c.address]).length;
    const allLive = coins.length > 0 && liveCount === coins.length;

    // The books already carry each token's freshness-gated quote, so prefer them
    // over the 5s REST poll — otherwise the table visibly lags the order book.
    // Ordered by the token list rather than by whichever source supplied a row,
    // so the table doesn't reshuffle when a poll lands.
    const oracleQuotes = useMemo(() => {
        const merged = new Map(oracles.map((q) => [q.token.toLowerCase(), q]));
        for (const book of Object.values(orderBooks)) {
            if (book.oracle) merged.set(book.token.toLowerCase(), book.oracle);
        }
        const ordered = coins
            .map((c) => merged.get(c.address.toLowerCase()))
            .filter((q): q is OracleQuote => q !== undefined);
        const known = new Set(coins.map((c) => c.address.toLowerCase()));
        const extras = [...merged.entries()]
            .filter(([address]) => !known.has(address))
            .map(([, q]) => q);
        return [...ordered, ...extras];
    }, [oracles, orderBooks, coins]);

    return (
        <div className="app">
            <header>
                <h1>Prism DEX</h1>
                <div className="header-meta">
                    <ChainSelector chains={chains} selected={chain} onSelect={setChain} />
                    <span
                        className={`conn ${
                            allLive ? 'on' : liveCount > 0 ? 'partial' : 'off'
                        }`}
                    >
                        {allLive
                            ? '● live'
                            : liveCount > 0
                            ? `◐ ${liveCount}/${coins.length} live`
                            : '○ offline'}
                    </span>
                </div>
            </header>
            {error && <div className="error">{error}</div>}
            <div className="layout">
                <aside>
                    <h2>Tokens</h2>
                    <TokenSelector coins={coins} selected={token} onSelect={setToken} />
                </aside>
                <main>
                    {/* Oracle prices sit between the book and the trades: the book's
                        levels are derived from them, so they read top-down. */}
                    {token && <OrderBookView orderBook={orderBook} token={token} />}
                    <OracleTable quotes={oracleQuotes} coins={coins} />
                    {token && <TradeHistory trades={trades} token={token} coins={coins} />}
                </main>
            </div>
        </div>
    );
}
